//Customer booking view
import React from "react";
import {
  Box,
  Container,
  Typography,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EastIcon from "@mui/icons-material/East";
import SeatView from "./SeatView";
import dayjs from "dayjs";

function formatBookingDate(date) {
  if (date == null || date === "") return "";
  if (typeof date === "string") {
    const s = date.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  }
  const d = dayjs(date);
  if (d.isValid()) return d.format("YYYY-MM-DD");
  const nd = new Date(date);
  if (!Number.isNaN(nd.getTime())) {
    const y = nd.getFullYear();
    const m = String(nd.getMonth() + 1).padStart(2, "0");
    const day = String(nd.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  return "";
}

const legendItems = [
  { color: "#C65102", label: "Counter" },
  { color: "#1565C0", label: "Male" },
  { color: "#E91E96", label: "Female" },
  { color: "#CFD8DC", label: "Available" },
  { color: "#43A047", label: "Selected" },
];

export default function HorizontalNonLinearStepper({ item, pickup, drop, date }) {
  const formattedDate = formatBookingDate(date);

  return (
    <Box sx={{ bgcolor: "#F0F4F8", minHeight: "100vh", pt: 10 }}>
      {/* Gradient page header */}
      <Box
        sx={{
          background: "linear-gradient(150deg, #0D1B2A 0%, #1565C0 60%, #0288D1 100%)",
          py: 5,
          px: 2,
          textAlign: "center",
        }}
      >
        <Typography variant="h4" fontWeight={900} color="white" letterSpacing={1} gutterBottom>
          Book Your Seat
        </Typography>
        <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.65)" }}>
          Choose your preferred seat and complete your booking
        </Typography>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Trip summary strip */}
        <Box
          sx={{
            bgcolor: "white",
            borderRadius: 3,
            border: "1px solid rgba(0,0,0,0.07)",
            p: 3,
            mb: 3,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 3,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <DirectionsBusIcon sx={{ color: "#1565C0" }} />
            <Box>
              <Typography variant="caption" color="text.disabled" fontWeight={600}>BUS</Typography>
              <Typography variant="subtitle2" fontWeight={800} color="#0D1B2A">{item?.bus_number}</Typography>
            </Box>
          </Stack>

          <Divider orientation="vertical" flexItem />

          <Stack direction="row" alignItems="center" spacing={1}>
            <CalendarMonthIcon sx={{ color: "#1565C0" }} />
            <Box>
              <Typography variant="caption" color="text.disabled" fontWeight={600}>DATE</Typography>
              <Typography variant="subtitle2" fontWeight={800} color="#0D1B2A">{formattedDate || "—"}</Typography>
            </Box>
          </Stack>

          <Divider orientation="vertical" flexItem />

          <Stack direction="row" alignItems="center" spacing={1.5}>
            <LocationOnIcon sx={{ color: "#1565C0" }} />
            <Box>
              <Typography variant="caption" color="text.disabled" fontWeight={600}>FROM</Typography>
              <Typography variant="subtitle2" fontWeight={800} color="#0D1B2A">{pickup}</Typography>
            </Box>
            <EastIcon sx={{ color: "#90CAF9" }} />
            <Box>
              <Typography variant="caption" color="text.disabled" fontWeight={600}>TO</Typography>
              <Typography variant="subtitle2" fontWeight={800} color="#0D1B2A">{drop}</Typography>
            </Box>
          </Stack>

          <Chip
            label={item?.bus_type}
            size="small"
            sx={{ bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 700, ml: "auto" }}
          />
        </Box>

        {/* Seat legend */}
        <Box
          sx={{
            bgcolor: "white",
            borderRadius: 3,
            border: "1px solid rgba(0,0,0,0.07)",
            p: 2,
            mb: 3,
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            color="text.secondary"
            sx={{ textTransform: "uppercase", letterSpacing: 1, display: "block", mb: 1.5 }}
          >
            Seat Legend
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1.5}>
            {legendItems.map(({ color, label }) => (
              <Stack direction="row" alignItems="center" spacing={0.8} key={label}>
                <Box sx={{ width: 16, height: 16, borderRadius: 0.5, bgcolor: color, flexShrink: 0 }} />
                <Typography variant="body2" color="text.secondary" fontWeight={600}>{label}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>

        {/* Seat picker card */}
        <Box
          sx={{
            bgcolor: "white",
            borderRadius: 3,
            border: "1px solid rgba(0,0,0,0.07)",
            p: { xs: 2, md: 3 },
          }}
        >
          <SeatView
            item={item}
            userMode={true}
            pickup={pickup}
            drop={drop}
            date={formattedDate}
          />
        </Box>
      </Container>
    </Box>
  );
}
